import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { PageLoader } from "@/components/ui/page-loader";
import { useSecurityIssueByDisplayId, useUpdateSecurityIssue } from "@/hooks/useSecurityIssues";
import { parseDisplayId } from "@/services/securityIssuesService";
import { useMatchingSuppressionRules } from "@/hooks/useMatchingSuppressionRules";
import { SuppressionBanner } from "@/components/security/issue-detail/SuppressionBanner";
import { useIssueComments } from "@/hooks/useIssueComments";
import { IssueHeader } from "@/components/security/issue-detail/IssueHeader";
import { IssueActionBar } from "@/components/security/issue-detail/IssueActionBar";
import { OverviewTab } from "@/components/security/issue-detail/OverviewTab";
import { InvestigationTab } from "@/components/security/issue-detail/InvestigationTab";
import { RemediationTab } from "@/components/security/issue-detail/RemediationTab";
import { CommentsTab } from "@/components/security/issue-detail/CommentsTab";
import { HistoryTab } from "@/components/security/issue-detail/HistoryTab";

const TABS = ["overview", "investigation", "remediation", "comments", "history"] as const;
type TabId = (typeof TABS)[number];

const TAB_LABELS: Record<TabId, string> = {
  overview: "Overview",
  investigation: "Investigation",
  remediation: "Remediation",
  comments: "Comments",
  history: "History",
};

export function IssueDetailPage() {
  const { issueId } = useParams<{ issueId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>("overview");

  const { data: issue, isLoading, error } = useSecurityIssueByDisplayId(issueId ?? "");
  const numericIssueId = issueId ? parseDisplayId(issueId) : 0;
  const { data: commentsResponse } = useIssueComments(numericIssueId);
  const commentCount = commentsResponse?.total_count ?? 0;
  const { matchingRules } = useMatchingSuppressionRules(issue);
  const updateMutation = useUpdateSecurityIssue();

  const handleUnsuppress = () => {
    if (!numericIssueId || numericIssueId <= 0) return;
    updateMutation.mutate({ id: numericIssueId, data: { status: "OPEN" } });
  };

  if (isLoading) {
    return <PageLoader message="Loading issue details..." />;
  }

  if (error || !issue) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Issue Not Found</h2>
          <p className="text-muted-foreground mb-4">
            {error instanceof Error ? error.message : "The requested issue could not be found."}
          </p>
          <Button onClick={() => navigate("/issues")}>Back to Issues</Button>
        </div>
      </div>
    );
  }

  const getTabLabel = (tab: TabId) => {
    if (tab === "comments" && commentCount > 0) return `Comments (${commentCount})`;
    return TAB_LABELS[tab];
  };

  return (
    <div className="flex flex-1 flex-col gap-6 p-6 bg-gray-50">
      <IssueHeader issue={issue} />

      {(issue.status === "SUPPRESSED" || matchingRules.length > 0) && (
        <SuppressionBanner
          matchingRules={matchingRules}
          issueStatus={issue.status}
          onUnsuppress={handleUnsuppress}
          isUnsuppressing={updateMutation.isPending}
        />
      )}

      <IssueActionBar issue={issue} numericIssueId={numericIssueId} />

      <div className="w-full">
        <div className="bg-white border-b flex items-center gap-0">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              {getTabLabel(tab)}
            </button>
          ))}
        </div>

        {activeTab === "overview" && (
          <OverviewTab issue={issue} numericIssueId={numericIssueId} />
        )}
        {activeTab === "investigation" && <InvestigationTab issue={issue} numericIssueId={numericIssueId} />}
        {activeTab === "remediation" && <RemediationTab numericIssueId={numericIssueId} />}
        {activeTab === "comments" && (
          <CommentsTab numericIssueId={numericIssueId} commentCount={commentCount} />
        )}
        {activeTab === "history" && <HistoryTab issue={issue} numericIssueId={numericIssueId} />}
      </div>
    </div>
  );
}
